import React, { FunctionComponent, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GetResponsiveWidth } from '../../common/TabsComponent';
import { useResponsive } from '../../../contexts/ResponsiveContext';

export interface LogoItem {
    imageUrl: string;
    title: string;
    titleHeight: number;
    description: string;
    redirectUrl: string;
    isNavigate?: boolean;
}

interface LogoDisplayProps {
    logos: LogoItem[];
}

const LogoDisplay: FunctionComponent<LogoDisplayProps> = ({ logos }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const navigate = useNavigate();
    const { isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } =
        useResponsive();
    const handleTitleClick = (redirectUrl: string, isNavigate?: boolean) => {
        if (isNavigate) {
            navigate(redirectUrl);
        } else {
            window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const getCardHeight = (index: number) => {
        if (isScreenLg) {
            return hoveredIndex === index ? '220px' : '190px';
        } else if (isScreenMd) {
            return hoveredIndex === index ? '253px' : '223px';
        } else if (isScreenSm) {
            return hoveredIndex === index ? '260px' : '230px';
        } else if (isScreenXs) {
            return hoveredIndex === index ? '230px' : '210px';
        }
        return hoveredIndex === index ? '273px' : '243px';
    };

    return (
        <Box sx={{ width: '100%', backgroundColor: 'white' }}>
            <Box
                sx={{
                    display: 'flex',
                    margin: 'auto',
                    width: GetResponsiveWidth,
                }}
            >
                <Box
                    sx={{
                        paddingTop: '4rem',
                        height: isScreenLg ? '320px' : isScreenXxl || isScreenXl ? '383px' : 'auto',
                        gap: 2,
                        marginBottom: isScreenMd || isScreenSm ? '2rem' : '0',
                        // position: 'relative',
                    }}
                >
                    <Box
                        data-testid={'logos_box'}
                        sx={{
                            display: 'flex',
                            gap: isScreenMd ? 5 : 2,
                            justifyContent: isScreenSm || isScreenXs ? 'center' : 'space-between',
                            flexWrap: isScreenXxl || isScreenXl || isScreenLg ? 'unset' : 'wrap',
                            width: '100%',
                        }}
                    >
                        {logos.map((logo, index) => (
                            <Box
                                key={index}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                                sx={{
                                    width: isScreenXs
                                        ? '300px'
                                        : isScreenSm
                                          ? '203px'
                                          : isScreenMd
                                            ? '300px'
                                            : isScreenLg
                                              ? '140px'
                                              : isScreenXl
                                                ? '178px'
                                                : '184.2px',
                                    height: getCardHeight(index),
                                    flex: 'none',
                                    flexGrow: 0,
                                    position: 'relative',
                                    transition: 'height 0.3s ease',
                                    boxShadow:
                                        hoveredIndex === index
                                            ? '0 4px 8px rgba(0,0,0,0.2)'
                                            : 'none',
                                    marginBottom: isScreenSm || isScreenXs ? '1.5rem' : 0,
                                }}
                            >
                                {/* Card border */}
                                <Box
                                    sx={{
                                        boxSizing: 'border-box',
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#FFFFFF',
                                        borderTop: '0.8px solid rgba(119, 119, 119, 0.5)',
                                        borderLeft: '0.8px solid rgba(119, 119, 119, 0.5)',
                                        borderRight: '0.8px solid rgba(119, 119, 119, 0.5)',
                                        borderBottom:
                                            hoveredIndex === index
                                                ? 'none'
                                                : '0.8px solid rgba(119, 119, 119, 0.5)',
                                        borderRadius: '3px',
                                        zIndex: 1,
                                    }}
                                />

                                {/* Pic */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        position: 'relative', // Ensure the child element's absolute positioning works correctly
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={logo.imageUrl}
                                        alt={logo.title}
                                        onClick={() =>
                                            handleTitleClick(logo.redirectUrl, logo.isNavigate)
                                        }
                                        role="button"
                                        tabIndex={0}
                                        sx={{
                                            position: 'absolute',
                                            width: isScreenLg ? '94px' : '153px',
                                            height: isScreenLg ? '92px' : '148px',
                                            top: '-15px',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            zIndex: 1,
                                            '&:focus': {
                                                outline: '2px solid #004894',
                                            },
                                        }}
                                    />

                                    {/* Card Title */}
                                    <Typography
                                        onClick={() =>
                                            handleTitleClick(logo.redirectUrl, logo.isNavigate)
                                        }
                                        role="button"
                                        tabIndex={0}
                                        sx={{
                                            position: 'absolute',
                                            height: `${logo.titleHeight}px`,
                                            top: isScreenLg ? '94px' : '148px',
                                            fontFamily: 'Poppins',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            fontSize: isScreenLg || isScreenSm ? '15px' : '18px',
                                            lineHeight: isScreenLg || isScreenSm ? '18px' : '20px',
                                            textAlign: 'center',
                                            color: '#004894',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            zIndex: 1,
                                            '&:focus': {
                                                outline: '2px solid #004894',
                                            },
                                        }}
                                    >
                                        {logo.title}
                                    </Typography>
                                    {(isScreenMd || isScreenSm || isScreenXs) &&
                                        hoveredIndex === index && (
                                            <Box
                                                sx={{
                                                    marginTop: '8px',
                                                    top: '180px',
                                                    backgroundColor: 'white',
                                                    position: 'relative',
                                                    borderRight:
                                                        '0.8px solid rgba(119, 119, 119, 0.5)',
                                                    borderBottom:
                                                        '0.8px solid rgba(119, 119, 119, 0.5)',
                                                    borderLeft:
                                                        '0.8px solid rgba(119, 119, 119, 0.5)',
                                                    borderTop: 'none',
                                                    borderRadius: '3px',
                                                    padding: '0.75rem',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                                    zIndex: 20,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Poppins',
                                                        fontSize: '14px',
                                                        color: '#333',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {logo.description}
                                                </Typography>
                                            </Box>
                                        )}
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {(isScreenXxl || isScreenXl || isScreenLg) && hoveredIndex !== null && (
                        <Box
                            sx={{
                                top: '-2px',
                                left: 0,
                                width: '100%',
                                position: 'relative',
                                backgroundColor: 'white',
                                padding: '1rem',
                                border: '0.8px solid rgba(119, 119, 119, 0.5)',
                                boxShadow: '0 -4px 8px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.2)',
                                zIndex: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: isScreenLg ? '14px' : '16px',
                                    color: '#333',
                                    textAlign: 'center',
                                }}
                            >
                                {logos[hoveredIndex]?.description}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default LogoDisplay;
